/* eslint-disable max-len */
import {
  useEffect, useRef, useState,
} from 'react';
import {
  Box, Paper, Button, Typography,
} from '@mui/material';
import {
  addScoreToBoard, cancelScoreToBoard, stopGameCounter, resetScore, setPointsHistory, registerPointRally,
} from 'redux/slices';
import { useSelector, useDispatch } from 'react-redux';
import * as S from 'components/styles';

export function Score() {
  const lastButtonPressTimeRef = useRef({});
  const [spinTeamA, setSpinTeamA] = useState(false);
  const [spinTeamB, setSpinTeamB] = useState(false);

  const {
    gameStarted, scoreA, scoreB, pointsHistory,
  } = useSelector((state) => state.game);
  const teams = useSelector((state) => state.teams);
  const dispatch = useDispatch();

  const teamA = teams.find((t) => t.id === 1);
  const teamB = teams.find((t) => t.id === 2);
  const teamAWinLastGame = teamA?.winLastGame;
  const teamBWinLastGame = teamB?.winLastGame;
  const teamAScoreIsZero = scoreA === 0;
  const teamBScoreIsZero = scoreB === 0;

  const addScore = (team) => {
    if (gameStarted) {
      const newPointsHistory = [...pointsHistory, team];
      dispatch(setPointsHistory(newPointsHistory));
      dispatch(addScoreToBoard(team));
      dispatch(registerPointRally());
      if (team === 'A') {
        const isFirstScore = scoreA === 0;
        const lastScoreIsFromTeamA = newPointsHistory[newPointsHistory.length - 2] === 'A';
        if (isFirstScore && teamAWinLastGame && teamBScoreIsZero) return null;
        if (isFirstScore && !teamAWinLastGame) return null;
        if (!lastScoreIsFromTeamA) {
          setSpinTeamA(true);
          return setTimeout(() => setSpinTeamA(false), 5000);
        }
      }
      if (team === 'B') {
        const isFirstScore = scoreB === 0;
        const lastScoreIsFromTeamB = newPointsHistory[newPointsHistory.length - 2] === 'B';
        if (isFirstScore && teamBWinLastGame && teamAScoreIsZero) return null;
        if (isFirstScore && !teamBWinLastGame) return null;
        if (isFirstScore && teamBWinLastGame && !teamAScoreIsZero) {
          setSpinTeamB(true);
          return setTimeout(() => setSpinTeamB(false), 5000);
        }
        if (!lastScoreIsFromTeamB) {
          setSpinTeamB(true);
          return setTimeout(() => setSpinTeamB(false), 5000);
        }
      }
    }
    return null;
  };

  const cancelScore = (team) => {
    const teamScore = team === 'A' ? scoreA : scoreB;
    if (gameStarted && teamScore > 0) {
      // Não mutar o array (é o state imutável do Redux) — copia sem o último item.
      dispatch(setPointsHistory(pointsHistory.slice(0, -1)));
      dispatch(cancelScoreToBoard(team));
    }
  };

  const fontSize = { xs: '8rem', md: '25rem' };

  const handleKeyDown = (e) => {
    if (e.key === 'a' || e.key === 'A' || e.key === 'q' || e.key === 'z') {
      addScore('A');
    }
    if (e.key === 'ç' || e.key === 'Ç' || e.key === 'p' || e.key === '.') {
      addScore('B');
    }
    if (e.key === 's' || e.key === 'S' || e.key === 'w' || e.key === 'x') {
      cancelScore('A');
    }
    if (e.key === 'l' || e.key === 'L' || e.key === 'o' || e.key === ',') {
      cancelScore('B');
    }
    if (e.key === 'Enter') {
      // Enter também ativa botões focados; confirmar evita zerar o placar sem querer.
      if (window.confirm('Zerar o placar da partida?')) dispatch(resetScore());
    }
  };

  // Listeners registrados uma vez chamam sempre a versão mais recente dos handlers
  // (senão ficam com o placar/histórico do momento em que o jogo começou).
  const handlersRef = useRef({});
  handlersRef.current = { handleKeyDown, addScore, cancelScore };

  useEffect(() => {
    if (!gameStarted) {
      dispatch(stopGameCounter());
      return undefined;
    }
    const onKeyDown = (e) => handlersRef.current.handleKeyDown(e);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [gameStarted]);

  useEffect(() => {
    let rafId = null;

    const updateGamepadStatus = () => {
      const gamepads = navigator.getGamepads ? navigator.getGamepads() : [];
      const currentTime = Date.now();
      const { addScore: add, cancelScore: cancel } = handlersRef.current;
      Array.from(gamepads).forEach((gamepad) => {
        if (gamepad) {
          gamepad.buttons.forEach((button, index) => {
            if (button.pressed) {
              // Check if the button was pressed more than 1 second ago
              if (!lastButtonPressTimeRef.current[index] || currentTime - lastButtonPressTimeRef.current[index] > 1000) {
                if (index === 1) add('A');
                if (index === 2) add('B');
                if (index === 4) cancel('A');
                if (index === 5) cancel('B');
                lastButtonPressTimeRef.current[index] = currentTime; // Update the last pressed time
              }
            }
          });
        }
      });
      rafId = requestAnimationFrame(updateGamepadStatus);
    };

    // Um único loop por montagem; começa já se o gamepad conectou antes de abrir a tela.
    const startLoop = () => {
      if (rafId === null) rafId = requestAnimationFrame(updateGamepadStatus);
    };
    const gamepads = navigator.getGamepads ? Array.from(navigator.getGamepads()) : [];
    if (gamepads.some(Boolean)) startLoop();

    window.addEventListener('gamepadconnected', startLoop);

    return () => {
      window.removeEventListener('gamepadconnected', startLoop);
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, []);

  // A sincronização do placar remoto agora é feita globalmente no RemoteSync (useRemoteSync),
  // para marcar em qualquer aba — não só na do Marcador.

  return (
    <Paper
      elevation={3}
      sx={{
        backgroundColor: '#121212', color: '#ff9cbd', minHeight: '100vh', display: 'flex', justifyContent: 'space-around', alignItems: 'center',
      }}
    >
      <Box
        mb={3}
        ml="-3rem"
        display="flex"
        flexDirection="column"
        alignItems="center"
        width="50%"
      >
        <Box>
          {spinTeamA
            ? (
              <S.SpinningTypography
                onClick={() => addScore('A')}
                variant="h1"
                component="h1"
                fontSize={fontSize}
                textAlign="center"
              >
                {scoreA}
              </S.SpinningTypography>
            )
            : (
              <Typography
                onClick={() => addScore('A')}
                variant="h1"
                component="h1"
                fontSize={fontSize}
                textAlign="center"
              >
                {scoreA}
              </Typography>
            )}
        </Box>
        <Box>
          <Button disabled={scoreA === 0 || !gameStarted} size="large" style={{ height: 50 }} variant="contained" onClick={() => cancelScore('A')}>Cancelar ponto</Button>
        </Box>
      </Box>
      <Box
        sx={{
          display: 'flex', justifyContent: 'center', alignItems: 'center', width: 'auto', // Adjust this to manage the space the "X" takes
        }}
      >
        <Typography fontSize={{ xs: '3rem', md: '8rem' }} variant="h1" component="h1" textAlign="center">
          X
        </Typography>
      </Box>
      <Box
        mb={3}
        ml="-3rem"
        display="flex"
        flexDirection="column"
        alignItems="center"
        width="50%"
      >
        <Box>
          {spinTeamB
            ? (
              <S.SpinningTypography
                onClick={() => addScore('B')}
                variant="h1"
                component="h1"
                fontSize={fontSize}
                textAlign="center"
              >
                {scoreB}
              </S.SpinningTypography>
            )
            : (
              <Typography
                onClick={() => addScore('B')}
                variant="h1"
                component="h1"
                fontSize={fontSize}
                textAlign="center"
              >
                {scoreB}
              </Typography>
            )}
        </Box>
        <Box>
          <Button disabled={scoreB === 0 || !gameStarted} size="large" style={{ height: 50 }} variant="contained" onClick={() => cancelScore('B')}>Cancelar ponto</Button>
        </Box>
      </Box>
    </Paper>
  );
}
